import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  useTheme,
  TextField,
  Button,
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  TrendingUp,
  TrendingDown,
  School,
  EventAvailable,
  FilterList,
} from '@mui/icons-material';
import AttendanceCalendar from './AttendanceCalendar';
import useStore from '../store/useStore';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function AttendanceImpact({
  studentId,
  refreshKey,
  attendance,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const theme = useTheme();
  const { language } = useStore();
  const en = language === 'en';

  const fetchData = () => {
    setLoading(true);
    let url = `/api/analytics/impact/${studentId}?lang=${language}&`;
    if (startDate) url += `startDate=${startDate}&`;
    if (endDate) url += `endDate=${endDate}&`;

    fetch(url, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch data');
        return res.json();
      })
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, refreshKey]);

  if (loading && !data)
    return <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />;

  const {
    studentStats,
    classStats,
    timeline,
    insights,
    groupLabel,
    pearsonR,
    peers,
    correlation,
    templateName,
  } = data || {};
  const hasAtt = studentStats?.attendance != null;
  const hasMast = studentStats?.mastery != null;
  const canCompare =
    (hasAtt && classStats?.attendance != null) ||
    (hasMast && classStats?.mastered != null);

  // Chart Data
  const chartData = data
    ? {
        labels: timeline.map((t) => t.month),
        datasets: [
          {
            label: en ? 'Words mastered' : 'Освоено слов',
            data: timeline.map((t) => t.studentMasteredCum),
            borderColor: theme.palette.success.main,
            backgroundColor: theme.palette.success.main,
            yAxisID: 'y',
            tension: 0.3,
            spanGaps: true,
            fill: false,
          },
          {
            label: en
              ? `Group average (${groupLabel})`
              : `Ср. по группе (${groupLabel})`,
            data: timeline.map((t) => t.classMasteredCum),
            borderColor: theme.palette.success.light,
            borderDash: [5, 5],
            yAxisID: 'y',
            tension: 0.3,
            spanGaps: true,
          },
          {
            label: en ? 'Attendance %' : 'Посещаемость %',
            data: timeline.map((t) => t.studentAttendance),
            borderColor: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.main,
            yAxisID: 'y1',
            tension: 0.3,
            spanGaps: true,
          },
          {
            label: en
              ? `Group attendance (${groupLabel}) %`
              : `Посещаемость группы (${groupLabel}) %`,
            data: timeline.map((t) => t.classAttendance),
            borderColor: theme.palette.secondary.light,
            borderDash: [5, 5],
            yAxisID: 'y1',
            tension: 0.3,
            spanGaps: true,
          },
        ],
      }
    : null;

  const chartOptions = {
    responsive: true,
    spanGaps: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    stacked: false,
    plugins: {
      title: {
        display: true,
        text: en
          ? `Attendance and words mastered${data?.templateName ? ` — ${data.templateName}` : ''}`
          : `Посещаемость и освоение слов${data?.templateName ? ` — ${data.templateName}` : ''}`,
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: en
            ? 'Words mastered (as of last assessment)'
            : 'Освоено слов (по последней оценке)',
        },
        min: 0,
        ticks: { precision: 0 },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        title: {
          display: true,
          text: en ? 'Attendance (%)' : 'Посещаемость (%)',
        },
        min: 0,
        max: 100,
      },
    },
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {en ? 'Attendance and progress' : 'Посещаемость и успеваемость'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            type="date"
            label={en ? 'From' : 'С'}
            size="small"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            type="date"
            label={en ? 'To' : 'По'}
            size="small"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={fetchData}
            disabled={loading}
          >
            {en ? 'Apply' : 'Фильтр'}
          </Button>
          {(startDate || endDate) && (
            <Button
              variant="text"
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setLoading(true);
                fetch(`/api/analytics/impact/${studentId}?lang=${language}`, {
                  credentials: 'include',
                })
                  .then((res) => res.json())
                  .then((d) => {
                    setData(d);
                    setLoading(false);
                  });
              }}
            >
              {en ? 'Reset' : 'Сбросить'}
            </Button>
          )}
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {data && (
        <>
          {/* Insights Section */}
          {insights.map((insight, index) => (
            <Alert
              key={index}
              severity={insight.type === 'alert' ? 'warning' : insight.type}
              sx={{ mb: 4, borderRadius: 2 }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                {en ? 'Takeaway' : 'Вывод'}
              </Typography>
              {insight.text}
            </Alert>
          ))}

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Card
                elevation={0}
                variant="outlined"
                sx={{ borderRadius: 3, bgcolor: 'primary.50' }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {en ? 'Attendance' : 'Посещаемость'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventAvailable color="primary" />
                    <Typography variant="h4" fontWeight="bold">
                      {hasAtt ? `${Math.round(studentStats.attendance)}%` : '—'}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    {hasAtt
                      ? en
                        ? `${studentStats.attended} of ${studentStats.sessions} lessons`
                        : `${studentStats.attended} из ${studentStats.sessions} занятий`
                      : en
                        ? 'No lessons in this period'
                        : 'Нет занятий за период'}
                    {studentStats.excused > 0
                      ? en
                        ? ` · ${studentStats.excused} excused not counted`
                        : ` · ${studentStats.excused} по уважит. причине не учтено`
                      : ''}
                  </Typography>
                  {hasAtt && classStats.attendance != null && (
                    <Typography variant="caption" color="text.secondary">
                      {groupLabel}: {Math.round(classStats.attendance)}% (
                      {peers?.attendance ?? 0} {en ? 'students' : 'уч.'})
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Card
                elevation={0}
                variant="outlined"
                sx={{ borderRadius: 3, bgcolor: 'secondary.50' }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {en ? 'Words mastered' : 'Освоено слов'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <School color="secondary" />
                    <Typography variant="h4" fontWeight="bold">
                      {hasMast ? studentStats.mastered : '—'}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    {hasMast
                      ? `${templateName || ''}${
                          studentStats.mastery != null
                            ? ` · ${Math.round(studentStats.mastery)}%`
                            : ''
                        }`
                      : en
                        ? 'No scored assessment yet'
                        : 'Оценок пока нет'}
                  </Typography>
                  {hasMast && classStats.mastered != null ? (
                    <Typography variant="caption" color="text.secondary">
                      {en ? 'Same level' : 'Тот же уровень'}:{' '}
                      {classStats.mastered} ({peers?.mastery ?? 0}{' '}
                      {en ? 'students' : 'уч.'})
                    </Typography>
                  ) : (
                    hasMast && (
                      <Typography variant="caption" color="text.disabled">
                        {en
                          ? 'No other assessed students on this level'
                          : 'Нет других оценённых учеников на этом уровне'}
                      </Typography>
                    )
                  )}
                </CardContent>
              </Card>
            </Grid>

            {canCompare && (
              <Grid size={{ xs: 12, md: 3 }}>
                <Card elevation={0} variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {en ? 'Gap to the group' : 'Разрыв с группой'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {classStats.mastered != null && hasMast && (
                        <Box>
                          <Typography
                            variant="h5"
                            color={
                              studentStats.mastered >= classStats.mastered
                                ? 'success.main'
                                : 'text.secondary'
                            }
                          >
                            {studentStats.mastered >= classStats.mastered
                              ? '+'
                              : ''}
                            {studentStats.mastered - classStats.mastered}
                          </Typography>
                          <Typography variant="caption">
                            {en ? 'Words' : 'Слов'}
                          </Typography>
                        </Box>
                      )}
                      {classStats.attendance != null && hasAtt && (
                        <Box>
                          <Typography
                            variant="h5"
                            color={
                              studentStats.attendance >= classStats.attendance
                                ? 'success.main'
                                : 'error.main'
                            }
                          >
                            {studentStats.attendance >= classStats.attendance
                              ? '+'
                              : ''}
                            {Math.round(
                              studentStats.attendance - classStats.attendance
                            )}
                            {en ? ' pp' : ' п.п.'}
                          </Typography>
                          <Typography variant="caption">
                            {en ? 'Attendance' : 'Посещаемость'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid size={{ xs: 12, md: canCompare ? 3 : 6 }}>
              <Card elevation={0} variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {en ? 'Link to results (r)' : 'Связь с результатами (r)'}
                  </Typography>
                  {pearsonR != null ? (
                    <>
                      <Typography variant="h4" fontWeight="bold">
                        {pearsonR}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {en
                          ? `${correlation?.months} months of data. An observation, not proof of cause.`
                          : `Данные за ${correlation?.months} мес. Это наблюдение, а не доказательство причины.`}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      {en
                        ? `Shown only with ${correlation?.minMonths ?? 6}+ months of both attendance and assessments (now ${correlation?.months ?? 0}). Fewer points say nothing reliable.`
                        : `Показываем только при ${correlation?.minMonths ?? 6}+ месяцах и посещаемости, и оценок (сейчас ${correlation?.months ?? 0}). По меньшему числу точек надёжных выводов нет.`}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Main Chart */}
          <Paper
            variant="outlined"
            sx={{ p: 3, borderRadius: 4, height: 400, mb: 3 }}
          >
            {timeline.length > 0 ? (
              <Line data={chartData} options={chartOptions} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography color="text.secondary">
                  {en
                    ? 'Not enough data for a chart in this period.'
                    : 'Недостаточно данных для построения графика за этот период.'}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Attendance Calendar */}
          {attendance && attendance.length > 0 && (
            <AttendanceCalendar attendance={attendance} />
          )}
        </>
      )}
    </Box>
  );
}
